import { useState, useEffect, useMemo } from 'react';
import { Row, Col, Form, Button, Collapse } from 'react-bootstrap';
import Select, { components } from 'react-select';
import DatePicker from 'react-datepicker';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash.debounce';

import { defaultFilters } from '../../components/Reporting/defaultFilters';
import DateRangeSelect from './Filters/dateRangeSelect';
import { normalizeReportFilters, SELECT_FILTER_STYLE, } from './Filters/reportFilterRegistry';

export default function FilterBar({
  filters,
  setFilters,
  reportConfig,
  dynamicOptions = {},
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const filterDefs = useMemo(
    () => normalizeReportFilters(reportConfig),
    [reportConfig]
  );

  const primaryFilters = useMemo(
    () => filterDefs.filter((f) => f.section !== 'advanced'),
    [filterDefs]
  );

  const advancedFilters = useMemo(
    () => filterDefs.filter((f) => f.section === 'advanced'),
    [filterDefs]
  );

  const debouncedSetFilters = useMemo(() => {
    return debounce((field, value) => {
      setFilters((prev) => ({
        ...prev,
        [field]: value,
      }));
    }, 400);
  }, [setFilters]);

  useEffect(() => {
    return () => {
      debouncedSetFilters.cancel();
    };
  }, [debouncedSetFilters]);

  useEffect(() => {
    if (!reportConfig?.milestones?.length) return;

    let result = [];

    if (filters.cohortStart) {
      const startIndex = reportConfig.milestones.findIndex(
        (o) => o.value === filters.cohortStart
      );

      result = reportConfig.milestones.slice(
        startIndex === -1 ? 0 : startIndex + 1
      );
    } else {
      setFilters((prev) => ({
        ...prev,
        cohortStart: reportConfig.milestones[0].value,
      }));

      result = reportConfig.milestones.slice(1);
    }

    dynamicOptions.steps = [...result];

    if (filters.cohortStop) {
      const stopIndex = reportConfig.milestones.findIndex(
        (o) => o.value === filters.cohortStop
      );

      result = result.slice(0, stopIndex);
    } else {
      setFilters((prev) => ({
        ...prev,
        cohortStop: reportConfig.milestones.at(-1).value,
      }));
    }

    setFilters((prev) => ({
      ...prev,
      milestones: [filters.cohortStart, ...result.map((m) => m.value)],
    }));
  }, [
    filters.cohortStart,
    filters.cohortStop,
    reportConfig?.milestones,
    dynamicOptions,
    setFilters,
  ]);

  const handleMultiSelect = (field, selected = []) => {
    setFilters((prev) => ({
      ...prev,
      [field]: selected.map((s) => s.value),
    }));
  };

  const hasModifiedFilters = () => {
    return !isEqual(filters, defaultFilters(reportConfig?.id));
  };

  const CustomMultiValue = (props) => {
    const { index, selectProps } = props;
    const maxToShow = 2;
    const selectedCount = selectProps.value.length;

    if (index < maxToShow) {
      return <components.MultiValue {...props} />;
    }

    if (index === maxToShow) {
      return (
        <div className="react-select__multi-value">
          <div className="react-select__multi-value__label">
            +{selectedCount - maxToShow} more
          </div>
        </div>
      );
    }

    return null;
  };

  const CustomMultiValueRemove = () => null;

  const renderFilter = (def) => {
    if (!def?.type) return null;

    if (def.type === 'dateRange') {
      return (
        <Col
          md={filters.dateRange === 'custom' ? 1 : def.col || 2}
          key={def.id}
        >
          <DateRangeSelect
            filters={filters}
            setFilters={setFilters}
            reportConfig={reportConfig}
          />
        </Col>
      );
    }

    if (def.type === 'multiSelect') {
      const optionKey = def.optionKey || def.id;
      const options = dynamicOptions[optionKey] || [];
      const selectedValues = filters[def.id] || [];

      return (
        <Col md={def.col || 2} key={def.id}>
          <Form.Label>{def.label}</Form.Label>
          <Select
            isMulti
            options={options}
            placeholder={def.placeholder || def.label}
            onChange={(selected) => handleMultiSelect(def.id, selected)}
            value={options.filter((opt) => selectedValues.includes(opt.value))}
            className="react-select-container"
            classNamePrefix="react-select"
            components={{
              MultiValue: CustomMultiValue,
              MultiValueRemove: CustomMultiValueRemove,
            }}
            styles={SELECT_FILTER_STYLE}
          />
        </Col>
      );
    }

    if (def.type === 'numberRange') {
      return (
        <>
          <Col md={def.col || 2} key={`${def.id}-min`}>
            <Form.Label>{def.minLabel || `Min ${def.label}`}</Form.Label>
            <Form.Control
              type="number"
              min={def.min}
              max={def.max}
              step={def.step}
              size="sm"
              value={filters[def.minKey] || ''}
              onChange={(e) => debouncedSetFilters(def.minKey, e.target.value)}
            />
          </Col>

          <Col md={def.col || 2} key={`${def.id}-max`}>
            <Form.Label>{def.maxLabel || `Max ${def.label}`}</Form.Label>
            <Form.Control
              type="number"
              min={def.min}
              max={def.max}
              step={def.step}
              size="sm"
              value={filters[def.maxKey] || ''}
              onChange={(e) => debouncedSetFilters(def.maxKey, e.target.value)}
            />
          </Col>
        </>
      );
    }

    if (def.type === 'text') {
      return (
        <Col md={def.col || 2} key={def.id}>
          <Form.Label>{def.label}</Form.Label>
          <Form.Control
            type="text"
            size="sm"
            value={filters[def.id] || ''}
            placeholder={def.placeholder || def.label}
            onChange={(e) => debouncedSetFilters(def.id, e.target.value)}
          />
        </Col>
      );
    }

    if (def.type === 'singleSelect') {
      const optionKey = def.optionKey || def.id;
      const options = dynamicOptions[optionKey] || [];

      return (
        <Col md={def.col || 2} key={def.id}>
          <Form.Label>{def.label}</Form.Label>
          <Form.Select
            size="sm"
            value={filters[def.id] || ''}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                [def.id]: e.target.value,
              }))
            }
          >
            <option value="">All</option>
            {options.map((opt) => (
              <option key={`${def.id}-${opt.value}`} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </Col>
      );
    }

    return null;
  };

    return (
        <Form className="p-3 border rounded">
        {reportConfig?.milestones?.length ? (
            <Row className="g-2 mb-3">
                <Col md={2}>
                    <Form.Label>Starting Milestone</Form.Label>
                    <Form.Select
                        size="sm"
                        value={filters.cohortStart || ''}
                        onChange={(e) =>
                            setFilters((prev) => ({
                                ...prev,
                                cohortStart: e.target.value,
                            }))
                        }
                    >
                        {reportConfig.milestones.map((m, idx) => {
                            if (idx === reportConfig.milestones.length - 1) return null;

                            return (
                                <option key={`cohortStart-${m.value}`} value={m.value}>
                                    {m.label}
                                </option>
                            );
                        })}
                    </Form.Select>
                </Col>

                <Col md={2}>
                    <Form.Label>Final Milestone</Form.Label>
                    <Form.Select
                        size="sm"
                        value={filters.cohortStop || ''}
                        onChange={(e) =>
                            setFilters((prev) => ({
                                ...prev,
                                cohortStop: e.target.value,
                            }))
                        }
                    >
                        {(dynamicOptions.steps || []).map((m) => (
                            <option key={`cohortStop-${m.value}`} value={m.value}>
                                {m.label}
                            </option>
                        ))}
                    </Form.Select>
                </Col>

                <Col className="ps-4 d-flex align-items-end pb-2">
                    <div className="text-muted small">
                        <b>Includes</b>:{' '}
                        {filters.milestones?.map(
                            (m) =>
                                reportConfig.milestones?.find((f) => f.value === m)?.label
                            )
                            ?.filter(Boolean)
                            ?.join(' → ') || ''}
                    </div>
                </Col>
            </Row>
        ) : null}

        {filterDefs.length ? (
            <>
                <Row className="g-2 mb-3">
                    {primaryFilters.map((def) => renderFilter(def))}

                    {filters.dateRange === 'custom' && (
                    <>
                        <Col md={2}>
                            <Form.Label>Start Date</Form.Label>
                            <div className="form-control form-control-sm">
                                <DatePicker
                                    selected={filters.startDate}
                                    onChange={(date) =>
                                        setFilters((prev) => ({
                                            ...prev,
                                            startDate: date,
                                        }))
                                    }
                                    placeholderText="Start"
                                    className="border-0"
                                />
                            </div>
                        </Col>

                        <Col md={2}>
                            <Form.Label>End Date</Form.Label>
                            <div className="form-control form-control-sm">
                                <DatePicker
                                    selected={filters.endDate}
                                    onChange={(date) =>
                                        setFilters((prev) => ({
                                            ...prev,
                                            endDate: date,
                                        }))
                                    }
                                    placeholderText="End"
                                    className="border-0"
                                />
                            </div>
                        </Col>
                    </>
                    )}
                </Row>

                <Collapse in={showAdvanced}>
                    <div>
                        <Row className="g-2 mb-3">
                            {advancedFilters.map((def) => renderFilter(def))}
                        </Row>
                    </div>
                </Collapse>

                <Row>
                    <Col md="auto" className="d-flex align-items-start">
                    {advancedFilters.length > 0 && (
                        <Button
                            variant="link"
                            size="sm"
                            className="filter-toggle text-decoration-none"
                            onClick={() => setShowAdvanced((prev) => !prev)}
                        >
                            {showAdvanced ? '▲ Less Filters' : '▼ More Filters'}
                        </Button>
                    )}

                    {hasModifiedFilters() && (
                        <Button
                            variant="link"
                            size="sm"
                            className="filter-toggle text-decoration-none"
                            onClick={() => setFilters(defaultFilters(reportConfig.id))}
                        >
                            Reset Filters
                        </Button>
                    )}
                    </Col>
                </Row>
            </>
        ) : null}
        </Form>
    );
}